import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, TextArea, Dropdown, Typography, Divider } from '@douyinfe/semi-ui';
import { IconLink, IconEdit, IconDelete, IconPlus, IconSearch, IconQrCode, IconEyeOpened, IconCode } from '@douyinfe/semi-icons';
import QRCode from 'qrcode';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import PageSelector from '../../components/PageSelector';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../../utils/clipboard';

const JoinGroup = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [showQrcodeModal, setShowQrcodeModal] = useState(false);
    const [currentQrcodeUrl, setCurrentQrcodeUrl] = useState('');

    const statusMap = {
        0: { text: '显示', type: 'success' },
        1: { text: '隐藏', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/join_group/list_jg_group/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/add_jg_group/', {
                ...values,
                price: Math.floor(parseFloat(values.price) * 100), // 转换为分
                content: {
                    tpl: values.page?.pageId || '',
                    desc: values.page?.pageName || '',
                    html: ''
                }
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/edit_jg_group/', {
                ...values,
                uid: currentRecord.uid,
                price: Math.floor(parseFloat(values.price) * 100), // 转换为分
                content: {
                    tpl: values.page?.pageId || '',
                    desc: values.page?.pageName || '',
                    html: ''
                }
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/join_group/delete_jg_group/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的群');
            return;
        }
        try {
            const response = await request.post('/go/sp/join_group/batchedit_jg_group/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '群信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.img && <img src={record.img} alt={text} style={{ width: 40, height: 40, borderRadius: 4 }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>¥{(record.price / 100).toFixed(2)}</div>
                        {record.order_cnt > 0 && (
                            <div style={{ color: 'var(--semi-color-text-2)', cursor: 'pointer' }} onClick={() => window.open(`/orderlist?group_uids=${record.uid}`, '_blank')}>订单:{record.order_cnt}</div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    {record.content?.tpl && (
                        <Dropdown
                            trigger={'click'}
                            position={'bottomLeft'}
                            render={
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        icon={<IconEyeOpened />}
                                        onClick={() => {
                                            const token = localStorage.getItem('token');
                                            const sp_uid = token ? JSON.parse(token).s : '';
                                            window.open(`/fe/joingroup/group.html?sp_uid=${sp_uid}&uid=${record.uid}`, '_blank');
                                        }}
                                    >
                                        预览
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        icon={<IconQrCode />}
                                        onClick={() => {
                                            const token = localStorage.getItem('token');
                                            const sp_uid = token ? JSON.parse(token).s : '';
                                            const url = `/fe/joingroup/group.html?sp_uid=${sp_uid}&uid=${record.uid}`;
                                            QRCode.toDataURL(window.location.origin + url, { width: 200, margin: 1 })
                                                .then(dataUrl => setCurrentQrcodeUrl(dataUrl))
                                                .catch(err => Toast.error('二维码生成失败'));
                                            setShowQrcodeModal(true);
                                        }}
                                    >
                                        二维码
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        icon={<IconLink />}
                                        onClick={() => {
                                            const token = localStorage.getItem('token');
                                            const sp_uid = token ? JSON.parse(token).s : '';
                                            const url = window.location.origin + `/fe/joingroup/group.html?sp_uid=${sp_uid}&uid=${record.uid}`;
                                            copyToClipboard(url)
                                                .then(() => Toast.success('链接已复制到剪贴板'))
                                                .catch(() => Toast.error('复制失败'));
                                        }}
                                    >
                                        复制链接
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        icon={<IconCode />}
                                        onClick={() => window.open(`/h5editor?uid=${record.content.tpl}`, '_blank')}
                                    >
                                        编辑页面
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            }
                        >
                            <Button type='primary' icon={<IconEyeOpened />}>预览</Button>
                        </Dropdown>
                    )}
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconQrCode />}
                        onClick={() => navigate(`/qrcode?group_uid=${record.uid}`)}
                    >
                        {record.qrcode_cnt ? `二维码(${record.qrcode_cnt})` : <span style={{ color: 'var(--semi-color-danger)' }}>(点击设置二维码)</span>}
                    </Button>
                    <Popconfirm
                        title="确定要删除该群吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const [addFormData, setAddFormData] = useState({
        title: '',
        img: '',
        price: 0,
        sort: 0,
        status: 0,
        page: null
    });

    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        if (currentRecord) {
            setEditFormData({
                ...currentRecord,
                price: currentRecord.price / 100,
                page: currentRecord.content?.tpl ? {
                    pageId: currentRecord.content.tpl,
                    pageName: currentRecord.content.desc
                } : null
            });
        }
    }, [currentRecord]);

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const formData = isEdit ? editFormData : addFormData;
        const setFormData = isEdit ? setEditFormData : setAddFormData;

        const handleSubmit = () => {
            if (!formData.title) {
                Toast.error('请输入群名');
                return;
            }
            if (isEdit) {
                handleEdit(formData);
            } else {
                handleAdd(formData);
            }
        };

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>群名 <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                    <Input
                        value={formData.title}
                        onChange={value => setFormData(prev => ({ ...prev, title: value }))}
                        placeholder="请输入群名"
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>群头像</div>
                    <ImageSelector
                        value={formData.img}
                        onChange={value => setFormData(prev => ({ ...prev, img: value }))}
                        placeholder="请输入群头像URL"
                        width="100%"
                        previewSize={100}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>费用(元)</div>
                    <InputNumber
                        value={formData.price}
                        onChange={value => setFormData(prev => ({ ...prev, price: value }))}
                        placeholder="请输入费用"
                        min={0}
                        precision={2}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>排序</div>
                    <InputNumber
                        value={formData.sort}
                        onChange={value => setFormData(prev => ({ ...prev, sort: value }))}
                        placeholder="请输入排序值（大的在前面）"
                        min={0}
                        precision={0}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>状态</div>
                    <Select
                        value={formData.status}
                        onChange={value => setFormData(prev => ({ ...prev, status: value }))}
                        style={{ width: '100%' }}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>关联页面</div>
                    <PageSelector
                        value={formData.page}
                        onChange={value => setFormData(prev => ({ ...prev, page: value }))}
                    />
                </div>
                <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' onClick={handleSubmit}>确定</Button>
                    </Space>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.joinGroupContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索群名"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="群状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增群</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个群吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增群"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑群"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>

            <Modal
                title="扫码预览"
                visible={showQrcodeModal}
                onCancel={() => setShowQrcodeModal(false)}
                footer={null}
                centered
                width={400}
            >
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <img
                        src={currentQrcodeUrl}
                        alt="二维码"
                        style={{ width: 200, height: 200 }}
                    />
                    <Typography.Text style={{ display: 'block', marginTop: 16, color: 'var(--semi-color-text-2)' }}>
                        扫描二维码预览页面
                    </Typography.Text>
                </div>
            </Modal>
        </div>
    );
};

export default JoinGroup;